import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Typography,
  Row,
  Col,
  Tag,
  Input,
  Select,
  Layout,
  Form,
  Flex,
  Upload,
  message,
  Modal
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import MapImg from "../../assets/images/map.svg";

import { formatVenueData } from "../../utils/payloadBuilderUtil";
import { VALIDATION_MESSAGES } from "../../utils/constants";
import GifLoader from "../../components/common/GifLoader";
import PageLayout from "../../components/common/Layouts/PageLayout";
import HelmetWrapper from "../../components/common/Layouts/HelmetWrapper";
import {
  createVenue,
  updateVenue,
  fetchVenueById,
  selectCreateVenueLoading,
  selectCreateVenueError,
  selectVenueStatus,
  selectUpdateVenueLoading,
  selectUpdateVenueError,
  selectUpdateVenueStatus,
  selectCurrentVenue,
  clearCreateVenueError,
  clearUpdateVenueError,
  clearCurrentVenue,
  resetStatus,
  resetUpdateVenueStatus
} from "../../redux/venueSlice";
import {
  fetchCountries,
  fetchStatesByCountry,
  fetchCitiesByState,
  selectCountries,
  selectCountriesLoading,
  selectStates,
  selectStatesLoading,
  selectCities,
  selectCitiesLoading,
  selectSelectedCountry,
  selectSelectedState,
  selectSelectedCity,
} from "../../redux/locationSlice";

const { Text } = Typography;
const { Content } = Layout;

/**
 * CREATE NEW VENUE PAGE
 * Uses Location APIs for Country, State, City dropdowns
 */
export default function VenueAddEditPage({ isEmbedded = false, onNext }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();

  const { id: venueId } = useParams(); // Get venueId from URL for edit mode
  const isEditMode = Boolean(venueId);

  // Create mode selectors
  const createVenueLoading = useSelector(selectCreateVenueLoading);
  const createVenueError = useSelector(selectCreateVenueError);
  const createVenueStatus = useSelector(selectVenueStatus);

  // Edit mode selectors
  const updateVenueLoading = useSelector(selectUpdateVenueLoading);
  const updateVenueError = useSelector(selectUpdateVenueError);
  const updateVenueStatus = useSelector(selectUpdateVenueStatus);

  /** Fetch pre-required data from redux **/
  // venue details
  const currentVenue = useSelector(selectCurrentVenue);
  // location data
  const countries = useSelector(selectCountries);
  const countriesLoading = useSelector(selectCountriesLoading);
  const states = useSelector(selectStates);
  const statesLoading = useSelector(selectStatesLoading);
  const cities = useSelector(selectCities);
  const citiesLoading = useSelector(selectCitiesLoading);
  // User's saved location data
  const savedCountry = useSelector(selectSelectedCountry);
  const savedState = useSelector(selectSelectedState);
  const savedCity = useSelector(selectSelectedCity);

  // State variables
  const [actionType, setActionType] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [gifLoader, setGifLoader] = useState(false);

  // Available tags for venue
  const AVAILABLE_TAGS = [
    'Banquet', 'Hall', 'Mumbai', 'Thane',
    'WeddingHall', 'Party Hall', 'Conference', 'Outdoor',
    'Indoor', 'Corporate', 'Birthday', 'Anniversary'
  ];

  // Fetch countries on component mount
  useEffect(() => {
    dispatch(fetchCountries());

    // If in edit mode, fetch the venue data
    if (isEditMode && venueId) {
      dispatch(fetchVenueById(venueId));
    }
  }, [dispatch, isEditMode, venueId]);

  // Pre-fill form with user's saved location for new venues
  useEffect(() => {
    if (!isEditMode && savedCountry && countries.length > 0) {
      // Set the country
      form.setFieldsValue({ country: savedCountry.id });

      // Fetch states for the saved country
      if (savedCountry.code) {
        dispatch(fetchStatesByCountry(savedCountry.code));
      }

      // Set state if available
      if (savedState) {
        form.setFieldsValue({ state: savedState.id });
        // Fetch cities for the saved state
        dispatch(fetchCitiesByState(savedState.id));
      }

      // Set city if available
      if (savedCity) {
        form.setFieldsValue({ city: savedCity.id });
      }
    }
  }, [isEditMode, savedCountry, savedState, savedCity, countries, form, dispatch]);

  // Pre-fill form when venue data is loaded in edit mode
  useEffect(() => {
    if (isEditMode && currentVenue && countries.length > 0) {
      // Get the country ID from venue data
      const countryId = currentVenue.country?.id || currentVenue.country_id;
      console.log('Extracted countryId:', countryId);

      // Set form values from venue data (except location fields - handled separately)
      form.setFieldsValue({
        venueName: currentVenue.name,
        venueDescription: currentVenue.description,
        addressLine1: currentVenue.address_line1,
        addressLine2: currentVenue.address_line2,
        pincode: currentVenue.pincode,
        landmark: currentVenue.landmark,
        capacity: currentVenue.capacity,
        country: countryId,
      });

      // Set tags if present
      if (currentVenue.tags) {
        const tagArray = currentVenue.tags.split(',').map(t => t.trim()).filter(t => t);
        setSelectedTags(tagArray);
      }

      // Fetch states for the country (state/city will be set once data loads)
      if (countryId) {
        const selectedCountry = countries.find(c => c.id === countryId);
        console.log('Found country match:', selectedCountry);
        if (selectedCountry?.code) {
          dispatch(fetchStatesByCountry(selectedCountry.code));
        } else {
          console.warn('No country code found for countryId:', countryId);
        }
      } else {
        console.warn('No countryId extracted from venue data');
      }
    }
  }, [isEditMode, currentVenue, countries, form, dispatch]);

  // Pre-fill venue's state field when states data is loaded in edit mode
  useEffect(() => {
    if (isEditMode && currentVenue && states.length > 0) {
      const stateId = currentVenue.state?.id || currentVenue.state;
      if (stateId) {
        // Set state value
        form.setFieldsValue({ state: stateId });
        // Fetch cities for the state
        dispatch(fetchCitiesByState(stateId));
      }
    }
  }, [isEditMode, currentVenue, states, form, dispatch]);

  // Pre-fill venue's city field when cities data is loaded in edit mode
  useEffect(() => {
    if (isEditMode && currentVenue && cities.length > 0) {
      const cityId = currentVenue.city?.id || currentVenue.city;
      if (cityId) {
        form.setFieldsValue({ city: cityId });
      }
    }
  }, [isEditMode, currentVenue, cities, form]);

  /**
   * Handlers
   */
  // Handle tag selection
  const handleTagClick = (tag) => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        // Remove tag if already selected
        return prev.filter(t => t !== tag);
      } else {
        // Add tag if not selected
        return [...prev, tag];
      }
    });
  };

  // Handle country selection - fetch states
  const handleCountryChange = (value) => {
    // Clear state and city when country changes
    form.setFieldsValue({ state: undefined, city: undefined });
    // Fetch states for selected country (using country code)
    const selectedCountry = countries.find(c => c.id === value);
    if (selectedCountry?.code) {
      dispatch(fetchStatesByCountry(selectedCountry.code));
    }
  };

  // Handle state selection - fetch cities
  const handleStateChange = (value) => {
    // Clear city when state changes
    form.setFieldsValue({ city: undefined });
    // Fetch cities for selected state
    dispatch(fetchCitiesByState(value));
  };

  // Handle redirect on successful venue creation
  useEffect(() => {
    if (!isEditMode && createVenueStatus === 'succeeded') {
      setTimeout(() => {
        setGifLoader(false);
        dispatch(clearCurrentVenue());
        dispatch(resetStatus());
        form.resetFields();

        if (isEmbedded && onNext) {
          onNext(); // Go to next step in Stepper
        } else {
          navigate('/venues');
        }
      }, 5000);
    }
  }, [createVenueStatus, dispatch, form, navigate, isEditMode, isEmbedded, onNext]);

  // Handle redirect on successful venue update
  useEffect(() => {
    if (isEditMode && updateVenueStatus === 'succeeded' && actionType === 'publish') {
      setTimeout(() => {
        setGifLoader(false);
        dispatch(clearCurrentVenue());
        dispatch(resetUpdateVenueStatus());
        form.resetFields();
        navigate('/venues');
      }, 5000);
    }
  }, [updateVenueStatus, dispatch, form, navigate, isEditMode]);

  // Handle create error
  useEffect(() => {
    if (createVenueError) {
      modal.error({
        icon: null,
        title: null,
        content: (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div>{createVenueError || 'Failed to create venue'}</div>
          </div>
        ),
        onOk: () => {
          setGifLoader(false);
          dispatch(clearCreateVenueError());
        },
      });
    }
  }, [createVenueError]);

  // Handle update error
  useEffect(() => {
    if (updateVenueError) {
      modal.error({
        icon: null,
        title: null,
        content: (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div>{updateVenueError || 'Failed to update venue'}</div>
          </div>
        ),
        onOk: () => {
          setGifLoader(false);
          dispatch(clearUpdateVenueError());
        },
      });
    }
  }, [updateVenueError]);

  /**
  * Handles form submission for both Create and Update scenarios.
  */
  const onFinish = async (values) => {
    try {
      setActionType('publish');
      setGifLoader(true);
      // Add selected tags to values
      const valuesWithTags = { ...values, tags: getTagsString() };
      const formattedValues = await formatVenueData(valuesWithTags, false, isEditMode ? 'update' : 'create');

      if (isEditMode) {
        dispatch(updateVenue(venueId, formattedValues));
      } else {
        dispatch(createVenue(formattedValues));
      }
    } catch (error) {
      console.error('Error saving venue:', error);
    }
  };

  /**
   * Handles form submission for draft save.
   */
  const handleSaveDraft = async () => {
    try {
      setActionType('draft');
      setGifLoader(true);
      const values = form.getFieldsValue();
      // Add selected tags to values
      const valuesWithTags = { ...values, tags: getTagsString() };
      const formattedValues = await formatVenueData(valuesWithTags, true, 'create');

      console.log('Saving Draft:', formattedValues);
      dispatch(createVenue(formattedValues));
    } catch (error) {
      console.error('Error saving draft:', error);
    }
  };

  /**
   * Helper Functions
   */

  // Get tags as comma-separated string for API
  const getTagsString = () => selectedTags.join(',');

  const Wrapper = ({ children }) => {
    if (isEmbedded) {
      return <>{children}</>;
    }
    return (
      <Content className={gifLoader ? "is-loading-blur" : ""}>
        <div className="pageCard">
          {children}
        </div>
      </Content>
    );
  };

  const content = (
    <>
      {contextHolder}
      {gifLoader && (
        <div className="loader-fixed">
          <GifLoader />
        </div>
      )}
      {/* SEO HELMET WRAPPER */}
      {!isEmbedded && (
        <HelmetWrapper
          title="Pinginvite - The Go-To Platform for Local Events in Kolkata & Dubai"
          description="Pingvite connects people with local events in Kolkata and Dubai through community focus, affordability, 
            and city-specific visibility for organizers and users."
          keywords="Top Upcoming Events in Kolkata, Best Live Events in Kolkata, best upcoming events in Kolkata, 
            online events near you in Kolkata,  upcoming events in Kolkata, Events in Kolkata, Top Upcoming Events in Dubai, 
            Best Live Events in Dubai, best upcoming events in Dubai, online events near you in Dubai, Event calendar in Dubai, 
            Event tickets in Dubai, upcoming events in Dubai, Events in Dubai"
        />
      )}
      <Wrapper>
        <Row gutter={[24, 24]}>
          <Col xs={24} md={16}>
            <Form layout="vertical" form={form} onFinish={onFinish}>
              {/* Venue Name */}
              <Form.Item
                label="Venue Name"
                name="venueName"
                rules={[{ required: true, message: VALIDATION_MESSAGES.VENUE_NAME_REQUIRED }]}
              >
                <Input placeholder="Venue Name" size="large" />
              </Form.Item>

              {/* Venue Description */}
              <Form.Item
                label="Venue Description"
                name="venueDescription"
                rules={[{ required: true, message: VALIDATION_MESSAGES.VENUE_DESCRIPTION_REQUIRED }]}
              >
                <Input.TextArea placeholder="Venue Description" rows={4} size="large" />
              </Form.Item>

              {/* Upload Images */}
              <Form.Item
                label="Upload Images"
                name="venueImages"
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) return e;
                  return e && e.fileList;
                }}
              >
                <Upload
                  name="images"
                  listType="picture"
                  accept="image/*"
                  beforeUpload={(file) => {
                    const isImage = file.type.startsWith('image/');
                    if (!isImage) {
                      message.error(`${file.name} is not a valid image file!`);
                      return Upload.LIST_IGNORE;
                    }
                    return false;
                  }}
                >
                  <Button icon={<UploadOutlined />}>Click to upload</Button>
                </Upload>
              </Form.Item>

              {/* Address Line 1 */}
              <Form.Item
                label="Address Line 1"
                name="addressLine1"
                rules={[{ required: true, message: VALIDATION_MESSAGES.ADDRESS_LINE1_REQUIRED }]}
              >
                <Input placeholder="Address Line 1" size="large" />
              </Form.Item>

              {/* Address Line 2 */}
              <Form.Item label="Address Line 2" name="addressLine2">
                <Input placeholder="Address Line 2" size="large" />
              </Form.Item>

              <Row gutter={[24, 0]}>
                {/* Country - API driven */}
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Country"
                    name="country"
                    rules={[{ required: true, message: VALIDATION_MESSAGES.COUNTRY_REQUIRED }]}
                  >
                    <Select
                      size="large"
                      placeholder="Select Country"
                      loading={countriesLoading}
                      onChange={handleCountryChange}
                      showSearch
                      optionFilterProp="children"
                    >
                      {Array.isArray(countries) && countries.map((country) => (
                        <Select.Option key={country.id} value={country.id}>
                          {country.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                {/* State - API driven */}
                <Col xs={24} md={12}>
                  <Form.Item
                    label="State"
                    name="state"
                    rules={[{ required: true, message: VALIDATION_MESSAGES.STATE_REQUIRED }]}
                  >
                    <Select
                      size="large"
                      placeholder="Select State"
                      loading={statesLoading}
                      onChange={handleStateChange}
                      showSearch
                      optionFilterProp="children"
                    >
                      {Array.isArray(states) && states.map((state) => (
                        <Select.Option key={state.id} value={state.id}>
                          {state.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                {/* City - API driven */}
                <Col xs={24} md={12}>
                  <Form.Item
                    label="City"
                    name="city"
                    rules={[{ required: true, message: VALIDATION_MESSAGES.CITY_REQUIRED }]}
                  >
                    <Select
                      size="large"
                      placeholder="Select City"
                      loading={citiesLoading}
                      showSearch
                      optionFilterProp="children"
                    >
                      {Array.isArray(cities) && cities.map((city) => (
                        <Select.Option key={city.id} value={city.id}>
                          {city.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                {/* Pincode */}
                <Col xs={24} md={12}>
                  <Form.Item label="Pincode" name="pincode">
                    <Input placeholder="Pincode" size="large" maxLength={6} />
                  </Form.Item>
                </Col>

                {/* Landmark */}
                <Col xs={24} md={12}>
                  <Form.Item label="Landmark" name="landmark">
                    <Input placeholder="Landmark" size="large" />
                  </Form.Item>
                </Col>

                {/* Capacity */}
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Capacity"
                    name="capacity"
                    rules={[{ required: true, message: VALIDATION_MESSAGES.CAPACITY_REQUIRED }]}
                  >
                    <Input type="number" placeholder="Capacity" size="large" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>

          {/* Right Side - Map & Tags */}
          <Col xs={24} md={8}>
            <Flex vertical gap={12}>
              <div className="mapPreview">
                <img src={MapImg} alt="Map preview" />
              </div>
              <Flex vertical gap={4}>
                <Text>Tags {selectedTags.length > 0 && `(${selectedTags.length} selected)`}</Text>
                <div className="tagsWrapper">
                  {AVAILABLE_TAGS.map((tag) => (
                    <Tag
                      key={tag}
                      color={selectedTags.includes(tag) ? 'blue' : 'default'}
                      className="pill-tag"
                      style={{
                        cursor: 'pointer',
                        border: selectedTags.includes(tag) ? '2px solid #1890ff' : '1px solid #d9d9d9',
                        fontWeight: selectedTags.includes(tag) ? 'bold' : 'normal',
                      }}
                      onClick={() => handleTagClick(tag)}
                    >
                      {tag}
                    </Tag>
                  ))}
                </div>
                {selectedTags.length > 0 && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Selected: {getTagsString()}
                  </Text>
                )}
              </Flex>
            </Flex>
          </Col>
        </Row>

        <div
          className={`addEditStickyActions${isEmbedded ? " addEditStickyActions--embedded" : ""}`}
        >
          {!isEditMode && !isEmbedded && (
            <Button className="draftBtn" onClick={handleSaveDraft}>
              Save as Draft
            </Button>
          )}
          {isEditMode ? (
            <Button
              type="primary"
              size="large"
              className="btnGradient publishBtn"
              onClick={() => form.submit()}
              loading={updateVenueLoading}
            >
              Update
            </Button>
          ) : (
            <Button
              type="primary"
              size="large"
              className="btnGradient publishBtn"
              onClick={() => form.submit()}
              loading={createVenueLoading}
            >
              {isEmbedded ? "Create Venue & Proceed" : "Publish"}
            </Button>
          )}
        </div>
      </Wrapper>
    </>
  );

  if (isEmbedded) {
    return content;
  }

  return (
    <PageLayout title={isEditMode ? "Edit Venue" : "New Venue"}>
      {content}
    </PageLayout>
  );
}
